<template>
  <div v-show="isOpen" class="archive-search-modal-overlay" @click.self="close">
    <div class="archive-search-modal-content">
      <h2 class="sr-only">{{ t("web.search.title") }}</h2>
      <form class="archive-search-modal-form" @submit.prevent="handleSubmit">
        <div class="archive-search-modal-input-wrapper">
          <div class="archive-search-modal-icon">
            <svg
              v-if="isLoading"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="archive-search-modal-spinner"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
          </div>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            class="archive-search-modal-input"
            :placeholder="t('web.form.placeholder.search')"
            autofocus
            autocomplete="off"
            @keydown.enter="handleKeydownEnter"
            @input="handleInput"
          />
        </div>
      </form>
      <div
        v-if="searchQuery.trim() && !isLoading && results.length === 0"
        class="archive-search-modal-empty"
      >
        {{ noResultsText }}
      </div>
      <div v-if="results.length > 0" class="archive-search-modal-results">
        <a
          v-for="result in results"
          :key="result.id"
          :href="result.absolute_url"
          class="archive-search-modal-result-item"
          @click="handleResultClick"
        >
          <div class="archive-search-modal-result-title">
            {{ result.subject }}
          </div>
          <div
            v-if="result.description"
            class="archive-search-modal-result-description"
          >
            {{ result.description }}
          </div>
          <div class="archive-search-modal-result-meta">
            {{ formatDate(result.publish_date || result.creation_date) }}
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "./lib/utils";

interface PublicEmail {
  id: string;
  subject: string;
  slug: string;
  publish_date: string | null;
  creation_date: string;
  description: string | null;
  absolute_url: string;
}

interface Props {
  newsletter: Newsletter;
}

const props = defineProps<Props>();

const t = createTranslator(() => props.newsletter.locale);

const noResultsText = computed(() => {
  return t("web.search.no_results").replace("{query}", searchQuery.value);
});

const emit = defineEmits<{
  close: [];
}>();

const isOpen = ref(false);
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const results = ref<PublicEmail[]>([]);
const isLoading = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const close = () => {
  isOpen.value = false;
  searchQuery.value = "";
  results.value = [];
  isLoading.value = false;
  if (searchTimeout) {
    clearTimeout(searchTimeout);
    searchTimeout = null;
  }
  emit("close");
};

const handleInput = () => {
  // Clear existing timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  const trimmedQuery = searchQuery.value.trim();

  if (!trimmedQuery) {
    results.value = [];
    return;
  }

  // Debounce search
  searchTimeout = setTimeout(() => {
    performSearch(trimmedQuery);
  }, 300);
};

const performSearch = async (query: string) => {
  if (!props.newsletter.username) {
    return;
  }

  isLoading.value = true;
  try {
    const apiUrl = `/v1/public/emails/${
      props.newsletter.username
    }?q=${encodeURIComponent(query)}`;
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error("Search failed");
    }

    const data = await response.json();
    results.value = data.results || [];
  } catch (error) {
    console.error("Search error:", error);
    results.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  // Map locale codes to BCP 47 format for proper date localization
  const localeMap: Record<string, string> = {
    "cs-CZ": "cs-CZ",
    de: "de-DE",
    en: "en-US",
    "en-AU": "en-AU",
    "en-CA": "en-CA",
    "en-GB": "en-GB",
    es: "es-ES",
    fr: "fr-FR",
    "nl-NL": "nl-NL",
    "pl-PL": "pl-PL",
    "pt-BR": "pt-BR",
    ru: "ru-RU",
    tr: "tr-TR",
  };
  const locale = localeMap[props.newsletter.locale] || "en-US";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const handleKeydownEnter = (e: KeyboardEvent) => {
  e.preventDefault();
  if (results.value.length > 0) {
    // Navigate to first result if available
    window.location.href = results.value[0].absolute_url;
  } else {
    handleSubmit();
  }
};

const handleResultClick = () => {
  close();
};

const handleSubmit = () => {
  const trimmedQuery = searchQuery.value.trim();
  if (!trimmedQuery) {
    return;
  }

  // Handle both relative and absolute URLs
  let url: URL;
  try {
    // Try to parse as absolute URL first
    url = new URL(props.newsletter.absolute_url);
  } catch {
    // If that fails, treat as relative URL
    const baseUrl = props.newsletter.absolute_url.startsWith("/")
      ? `${window.location.origin}${props.newsletter.absolute_url}`
      : `${window.location.origin}/${props.newsletter.absolute_url}`;
    url = new URL(baseUrl);
  }

  // Set the search parameter (this will replace any existing value)
  url.searchParams.set("q", trimmedQuery);

  // Navigate to the archive page with search results
  window.location.href = url.toString();
};

const handleKeydown = (e: KeyboardEvent) => {
  // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
  const isModifierKey = e.metaKey || e.ctrlKey;
  const isKKey = e.key === "k" || e.key === "K";

  if (isModifierKey && isKKey) {
    // Don't trigger if user is typing in an input, textarea, or contenteditable
    const target = e.target as HTMLElement;
    const isInputElement =
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable;

    if (!isInputElement) {
      e.preventDefault();
      isOpen.value = true;
      // Focus the input field after opening
      setTimeout(() => {
        searchInput.value?.focus();
      }, 0);
    }
  }

  // Handle Escape key
  if (e.key === "Escape" && isOpen.value) {
    close();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);

  document
    .querySelector(".archive-search-trigger")
    ?.addEventListener("click", () => {
      isOpen.value = true;
      // Focus the input field after opening
      setTimeout(() => {
        searchInput.value?.focus();
      }, 0);
    });
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.archive-search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.25s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.archive-search-modal-content {
  background-color: var(--color-background-card, #ffffff);
  border-radius: 8px;
  border: 1px solid var(--color-background-offset, #e5e7eb);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 600px;
  max-width: calc(100% - 8rem);
  animation: slideUp 0.25s ease-out;
  overflow: hidden;
  font-family: var(--font-family, sans-serif);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.archive-search-modal-form {
  display: flex;
  flex-direction: column;
}

.archive-search-modal-input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.archive-search-modal-icon {
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-foreground-offset, #6b7280);
  pointer-events: none;
}

.archive-search-modal-input {
  height: 48px;
  padding: 0 14px 0 42px;
  border: 0;
  background-color: transparent;
  font-size: 14px;
  color: var(--color-foreground, #111827);
  outline: none;
  flex: 1;
}

.archive-search-modal-input::placeholder {
  color: var(--color-foreground-offset, #6b7280);
}

.archive-search-modal-input:focus {
  outline: none;
}

.archive-search-modal-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.archive-search-modal-empty {
  padding: 24px;
  text-align: center;
  color: var(--color-foreground-offset, #6b7280);
  font-size: 14px;
}

.archive-search-modal-results {
  max-height: 400px;
  overflow-y: auto;
  border-top: 1px solid var(--color-background-offset, #e5e7eb);
}

.archive-search-modal-result-item {
  display: block;
  padding: 16px;
  border-bottom: 1px solid var(--color-background-offset, #e5e7eb);
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s;
}

.archive-search-modal-result-item:hover {
  background-color: var(--color-background-offset, #f9fafb);
}

.archive-search-modal-result-item:last-child {
  border-bottom: none;
}

.archive-search-modal-result-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground, #111827);
  margin-bottom: 4px;
}

.archive-search-modal-result-description {
  font-size: 13px;
  color: var(--color-foreground-offset, #6b7280);
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.archive-search-modal-result-meta {
  font-size: 12px;
  color: var(--color-foreground-offset, #9ca3af);
}
</style>
